import { LanguagePipe } from '../pipe/language.pipe';

/**
 * The measurement system currently being rendered in the UI.
 *
 * This is a *display* concern only. Every value in the model layer is
 * stored in one canonical unit regardless of what is on screen:
 *
 * - lengths        -> inches
 * - weights        -> pounds (lbs)
 * - volumes        -> cubic inches (in^3)
 * - book geometry  -> points (1/72 inch) -- `Book.body.width`,
 *                     `Book.body.height`, cover shifts, etc.
 *
 * Nothing here ever mutates stored data; it converts on the way out
 * (`toDisplay*`) and back on the way in (`fromDisplay*`).
 */
export type UnitSystem = 'IMPERIAL' | 'METRIC';

/** The kinds of unit label this utility knows how to resolve. */
export type MeasureKind = 'length' | 'weight' | 'volume' | 'pixel' | 'em' | 'point' | 'fileSize';

/**
 * Centralised unit conversion + unit-label lookup.
 *
 * WHY THIS EXISTS
 * ---------------
 * Before this class the strings `in`, `lbs`, `mm` and `in³` were typed
 * as bare text nodes directly into ~30 templates. They were invisible
 * to `ng extract-i18n` (nothing marks them), so they rendered English
 * in all 21 locales, and they hard-coded the imperial system with no
 * way to switch. `docs/i18n/untranslatable.md` is the audit that found
 * them.
 *
 * Everything now funnels through here, which gives us three things at
 * once:
 *
 *  1. **One rounding rule.** `length(v, 1)` always renders 1 decimal;
 *     the digit count is passed per call site and defaults to 2.
 *  2. **One place that knows imperial vs metric.** Flipping
 *     `StartupService.displayUnitSystem` re-renders every measurement
 *     in the app.
 *  3. **Translatable unit labels.** The label is a real i18n row
 *     (`@@unitLengthInch` etc.) resolved through the runtime
 *     `LanguagePipe` map, not a literal.
 *
 * LABEL CACHING
 * -------------
 * The labels are looked up from `LanguagePipe` **once** and cached,
 * then re-resolved only when `refresh()` is called -- which happens
 * when the unit system changes or the current Organization changes
 * (see `StartupService.setDisplayUnitSystem`). `AppComponent.ngOnInit`
 * also calls `refresh()` immediately after it seeds the
 * `LanguagePipe` map from the hidden `languageStringText` spans,
 * because any lookup made before that point would cache the raw id.
 *
 * NOT AN @Injectable ON PURPOSE
 * -----------------------------
 * Model classes (`src/model/**`) and pipes need these conversions and
 * do not receive services via DI. A static class is reachable from
 * everywhere; `StartupService` owns the *state* and pushes it in here.
 */
export class MeasurementUnits {

  // ── Canonical conversion constants ────────────────────────────────
  /** Exact, by definition (international inch, 1959). */
  public static readonly MM_PER_INCH = 25.4;
  /** PostScript/PDF point. Book geometry is stored in these. */
  public static readonly POINTS_PER_INCH = 72;
  /** Exact, by definition (international pound). */
  public static readonly KG_PER_POUND = 0.45359237;
  /** 25.4^3 -- cubic inches to cubic millimetres. */
  public static readonly MM3_PER_IN3 = 16387.064;
  /** 2.54^3 -- cubic inches to cubic centimetres. */
  public static readonly CM3_PER_IN3 = 16.387064;

  /**
   * Which metric volume unit to display.
   *
   * `cm3` is the default because `mm3` produces unreadable magnitudes
   * for package volumes (a 100 in³ carton is 1,638,706 mm³ but a
   * comfortable 1,638.7 cm³). Both labels are carried in every
   * translation bundle, so switching this constant is a one-line
   * change that needs no i18n work.
   */
  private static readonly METRIC_VOLUME_UNIT: 'mm3' | 'cm3' = 'cm3';

  // ── Translation ids ───────────────────────────────────────────────
  /**
   * The `@@id` of every unit label, per system.
   *
   * These are deliberately descriptive (`unitLengthInch`) rather than
   * the display text (`in`). `in` is also an English preposition and
   * `pt`/`em`/`px` are ambiguous out of context; a translator handed
   * the bare id would have no way to know which sense is meant. The
   * *value* carried in the bundles is still `in`, `mm`, ... -- per
   * AGENTS.md, translators work from the English VALUE, never the id.
   */
  public static readonly TRANSLATION_IDS: { [kind: string]: { IMPERIAL: string, METRIC: string } } = {
    length:   { IMPERIAL: 'unitLengthInch',      METRIC: 'unitLengthMillimeter' },
    weight:   { IMPERIAL: 'unitWeightPound',     METRIC: 'unitWeightKilogram' },
    volume:   { IMPERIAL: 'unitVolumeCubicInch', METRIC: MeasurementUnits.METRIC_VOLUME_UNIT === 'cm3'
                                                          ? 'unitVolumeCubicCentimeter'
                                                          : 'unitVolumeCubicMillimeter' },
    // System-independent labels: these are CSS/editor units and a
    // typographic point. They do not flip with imperial/metric, but
    // they are still display text and still need translating (several
    // scripts abbreviate them differently).
    pixel:    { IMPERIAL: 'unitLengthPixel',     METRIC: 'unitLengthPixel' },
    em:       { IMPERIAL: 'unitLengthEm',        METRIC: 'unitLengthEm' },
    point:    { IMPERIAL: 'unitLengthPoint',     METRIC: 'unitLengthPoint' },
    fileSize: { IMPERIAL: 'unitFileSizeMegabyte', METRIC: 'unitFileSizeMegabyte' },
  };

  /** Default number of decimal places when a call site does not say. */
  public static readonly DEFAULT_DIGITS = 2;

  // ── State ─────────────────────────────────────────────────────────
  private static system: UnitSystem = 'IMPERIAL';
  /** Resolved label cache, keyed by `MeasureKind`. Cleared by `refresh()`. */
  private static labelCache: { [kind: string]: string } = {};

  // ── System ────────────────────────────────────────────────────────

  /**
   * Coerce whatever the Organization record carries into a valid
   * `UnitSystem`. The server-side field does not exist yet, so this
   * has to tolerate `undefined`/`null` and a range of spellings; it
   * falls back to `IMPERIAL`, which is what the app rendered before
   * this utility existed.
   */
  public static normalizeUnitSystem(value: any): UnitSystem {
    if (value === true) { return 'METRIC'; }
    if (value === false || value === undefined || value === null) { return 'IMPERIAL'; }
    const text = ('' + value).trim().toUpperCase();
    switch (text) {
      case 'METRIC':
      case 'SI':
      case 'MM':
      case 'CM':
      case 'KG':
        return 'METRIC';
      default:
        return 'IMPERIAL';
    }
  }

  /**
   * Set the display system and drop the label cache. Safe to call
   * with the same value repeatedly -- it is also the hook used to
   * force a label re-read when the Organization changes but the
   * system happens not to.
   */
  public static setUnitSystem(system: UnitSystem): void {
    MeasurementUnits.system = MeasurementUnits.normalizeUnitSystem(system);
    MeasurementUnits.refresh();
  }

  public static getUnitSystem(): UnitSystem {
    return MeasurementUnits.system;
  }

  public static isMetric(): boolean {
    return MeasurementUnits.system === 'METRIC';
  }

  /**
   * Discard cached labels so the next `label()` re-reads them from
   * `LanguagePipe`. Call after the runtime translation map is seeded
   * or whenever the unit system / Organization changes.
   */
  public static refresh(): void {
    MeasurementUnits.labelCache = {};
  }

  // ── Labels ────────────────────────────────────────────────────────

  /**
   * The translated unit label for `kind` in the current system --
   * e.g. `in` / `mm`, `lbs` / `kg`, `in³` / `cm³`.
   *
   * Resolved once then cached; see the class comment.
   */
  public static label(kind: MeasureKind): string {
    if (MeasurementUnits.labelCache[kind] !== undefined) {
      return MeasurementUnits.labelCache[kind];
    }
    const ids = MeasurementUnits.TRANSLATION_IDS[kind];
    if (!ids) {
      MeasurementUnits.labelCache[kind] = '';
      return '';
    }
    const id = MeasurementUnits.isMetric() ? ids.METRIC : ids.IMPERIAL;
    const resolved = LanguagePipe.get(id);
    MeasurementUnits.labelCache[kind] = resolved;
    return resolved;
  }

  public static lengthLabel(): string { return MeasurementUnits.label('length'); }
  public static weightLabel(): string { return MeasurementUnits.label('weight'); }
  public static volumeLabel(): string { return MeasurementUnits.label('volume'); }

  /**
   * Translated length label for an **explicitly named** unit, ignoring
   * the current system.
   *
   * The cover/interior editors (`ResourceComponent`,
   * `ProjectDraftBodyComponent`, `SpreadComponent`) carry their own
   * in/mm toggle so an operator can flip rulers mid-edit without
   * touching the Organization-wide preference. Those call sites still
   * need the label translated, and it still has to come from one
   * place -- otherwise `mm` goes back to being a literal in the
   * template, which is exactly what `untranslatable.md` flagged.
   */
  public static lengthLabelFor(unit: 'in' | 'mm'): string {
    const id = unit === 'mm'
      ? MeasurementUnits.TRANSLATION_IDS['length'].METRIC
      : MeasurementUnits.TRANSLATION_IDS['length'].IMPERIAL;
    const cacheKey = 'explicit:' + unit;
    if (MeasurementUnits.labelCache[cacheKey] !== undefined) {
      return MeasurementUnits.labelCache[cacheKey];
    }
    const resolved = LanguagePipe.get(id);
    MeasurementUnits.labelCache[cacheKey] = resolved;
    return resolved;
  }

  // ── Conversion: stored -> display ─────────────────────────────────

  /** inches -> current display length unit. */
  public static toDisplayLength(inches: number): number {
    return MeasurementUnits.isMetric() ? inches * MeasurementUnits.MM_PER_INCH : inches;
  }

  /** points (1/72 in) -> current display length unit. */
  public static toDisplayLengthFromPoints(points: number): number {
    return MeasurementUnits.toDisplayLength(points / MeasurementUnits.POINTS_PER_INCH);
  }

  /** pounds -> current display weight unit. */
  public static toDisplayWeight(pounds: number): number {
    return MeasurementUnits.isMetric() ? pounds * MeasurementUnits.KG_PER_POUND : pounds;
  }

  /** cubic inches -> current display volume unit. */
  public static toDisplayVolume(cubicInches: number): number {
    if (!MeasurementUnits.isMetric()) { return cubicInches; }
    return MeasurementUnits.METRIC_VOLUME_UNIT === 'cm3'
      ? cubicInches * MeasurementUnits.CM3_PER_IN3
      : cubicInches * MeasurementUnits.MM3_PER_IN3;
  }

  // ── Conversion: display -> stored ─────────────────────────────────
  // The inverses of `toDisplay*`, for inputs that let an operator TYPE
  // a measurement. They have no caller yet, and are kept deliberately:
  // `ResourceComponent.onShiftInput()` and
  // `ProjectDraftBodyComponent.changeShiftValue()` already do this
  // conversion by hand against a local `unitMultiplier`, and they are
  // the intended migration target. Shipping `toDisplayLength` without
  // `fromDisplayLength` would leave half a round-trip in the one class
  // that is supposed to own the whole thing.

  /** current display length unit -> inches. */
  public static fromDisplayLength(value: number): number {
    return MeasurementUnits.isMetric() ? value / MeasurementUnits.MM_PER_INCH : value;
  }

  /** current display length unit -> points (1/72 in). */
  public static fromDisplayLengthToPoints(value: number): number {
    return MeasurementUnits.fromDisplayLength(value) * MeasurementUnits.POINTS_PER_INCH;
  }

  /** current display weight unit -> pounds. */
  public static fromDisplayWeight(value: number): number {
    return MeasurementUnits.isMetric() ? value / MeasurementUnits.KG_PER_POUND : value;
  }

  // ── Formatting ────────────────────────────────────────────────────

  /**
   * Round to `digits` decimals and localise the digit glyphs (e.g.
   * Arabic-Indic numerals in `ar`). Returns `''` for null/NaN so a
   * missing measurement renders as blank rather than "NaN in".
   */
  public static formatNumber(value: any, digits: number = MeasurementUnits.DEFAULT_DIGITS): string {
    if (value === undefined || value === null || value === '') { return ''; }
    const numeric = typeof value === 'number' ? value : parseFloat('' + value);
    if (!isFinite(numeric)) { return ''; }
    const safeDigits = (digits === undefined || digits === null || digits < 0) ? MeasurementUnits.DEFAULT_DIGITS : digits;
    return LanguagePipe.localizeNumber(numeric.toFixed(safeDigits));
  }

  /**
   * Glue a formatted number to its unit label. Kept in one place so
   * the separator (and, later, any RTL-specific handling) is
   * consistent everywhere.
   */
  public static withUnit(formattedValue: string, unitLabel: string): string {
    if (!formattedValue) { return ''; }
    if (!unitLabel) { return formattedValue; }
    return formattedValue + ' ' + unitLabel;
  }

  // ── Value-only helpers (no unit label) ────────────────────────────
  // For call sites like "8.5 × 11 in" where only the last number
  // carries the unit.

  /** inches -> "8.5" in display units. */
  public static lengthValue(inches: any, digits: number = MeasurementUnits.DEFAULT_DIGITS): string {
    if (inches === undefined || inches === null || inches === '') { return ''; }
    return MeasurementUnits.formatNumber(MeasurementUnits.toDisplayLength(Number(inches)), digits);
  }

  /** points -> "8.5" in display units. */
  public static pointsValue(points: any, digits: number = MeasurementUnits.DEFAULT_DIGITS): string {
    if (points === undefined || points === null || points === '') { return ''; }
    return MeasurementUnits.formatNumber(MeasurementUnits.toDisplayLengthFromPoints(Number(points)), digits);
  }

  /** pounds -> "0.55" in display units. */
  public static weightValue(pounds: any, digits: number = MeasurementUnits.DEFAULT_DIGITS): string {
    if (pounds === undefined || pounds === null || pounds === '') { return ''; }
    return MeasurementUnits.formatNumber(MeasurementUnits.toDisplayWeight(Number(pounds)), digits);
  }

  /** cubic inches -> "1638.7" in display units. */
  public static volumeValue(cubicInches: any, digits: number = 0): string {
    if (cubicInches === undefined || cubicInches === null || cubicInches === '') { return ''; }
    return MeasurementUnits.formatNumber(MeasurementUnits.toDisplayVolume(Number(cubicInches)), digits);
  }

  // ── Value + unit helpers ──────────────────────────────────────────
  // NOTE: named `formatLength`, not `length`. A static member called
  // `length` on a class collides with the built-in `Function.length`
  // and is a TypeScript error (TS2699). The weight/volume siblings are
  // named to match rather than being inconsistent about it.

  /** inches -> "8.5 in" / "215.9 mm". */
  public static formatLength(inches: any, digits: number = MeasurementUnits.DEFAULT_DIGITS): string {
    return MeasurementUnits.withUnit(MeasurementUnits.lengthValue(inches, digits), MeasurementUnits.lengthLabel());
  }

  /** points -> "8.5 in" / "215.9 mm". */
  public static formatLengthFromPoints(points: any, digits: number = MeasurementUnits.DEFAULT_DIGITS): string {
    return MeasurementUnits.withUnit(MeasurementUnits.pointsValue(points, digits), MeasurementUnits.lengthLabel());
  }

  /** pounds -> "0.55 lbs" / "0.25 kg". */
  public static formatWeight(pounds: any, digits: number = MeasurementUnits.DEFAULT_DIGITS): string {
    return MeasurementUnits.withUnit(MeasurementUnits.weightValue(pounds, digits), MeasurementUnits.weightLabel());
  }

  /** cubic inches -> "100 in³" / "1639 cm³". */
  public static formatVolume(cubicInches: any, digits: number = 0): string {
    return MeasurementUnits.withUnit(MeasurementUnits.volumeValue(cubicInches, digits), MeasurementUnits.volumeLabel());
  }
}





